import { supabase } from './supabase';

// Type definitions
export interface Message {
  id: string;
  content: string;
  user_id: string;
  created_at: string;
  [key: string]: any; // For any other fields in your messages table
}

export interface ThreadedMessage extends Message {
  parent_id: string | null;
  ancestors: string[];
  children?: ThreadedMessage[];
  depth: number;
  collapsed?: boolean;
}

/**
 * Extract thread metadata from message content
 * This allows us to implement threading without changing DB schema
 */
export function parseThreadMetadata(message: Message): ThreadedMessage {
  // Check for reply metadata in content using regex
  const replyMatch = message.content.match(/\[reply:([a-zA-Z0-9-]+)\]/);
  const ancestorsMatch = message.content.match(/\[ancestors:([a-zA-Z0-9,-]*)\]/);
  
  const parent_id = replyMatch ? replyMatch[1] : null;
  const ancestors = ancestorsMatch ? ancestorsMatch[1].split(',').filter(Boolean) : [];
  
  // Clean content for display (remove metadata)
  const cleanContent = message.content
    .replace(/\[reply:[a-zA-Z0-9-]+\]/, '')
    .replace(/\[ancestors:[a-zA-Z0-9,-]*\]/, '')
    .trim();
  
  return {
    ...message,
    content: cleanContent,
    parent_id,
    ancestors,
    depth: ancestors.length,
    children: []
  };
}

/**
 * Add thread metadata to message content
 * This encodes threading info without changing DB schema
 */
export function addThreadMetadata(
  content: string, 
  parentId: string | null = null, 
  ancestors: string[] = []
): string {
  let threadedContent = content.trim();
  
  // Add reply marker if there's a parent
  if (parentId) {
    threadedContent += `\n\n[reply:${parentId}]`;
  }
  
  // Add ancestors chain if there are any
  if (ancestors.length > 0) {
    threadedContent += `\n[ancestors:${ancestors.join(',')}]`;
  }
  
  return threadedContent;
}

/**
 * Build a thread tree from flat messages
 */
export function buildThreadTree(messages: Message[]): ThreadedMessage[] {
  // First parse all messages to extract threading metadata
  const threadedMessages = messages.map(parseThreadMetadata);
  
  // Map for quick lookups
  const messageMap = new Map<string, ThreadedMessage>();
  threadedMessages.forEach(msg => {
    messageMap.set(msg.id, { ...msg, children: [] });
  });
  
  // Root level messages (no parent)
  const rootMessages: ThreadedMessage[] = [];
  
  // Build the tree
  threadedMessages.forEach(msg => {
    const threadedMsg = messageMap.get(msg.id);
    if (!threadedMsg) return;
    
    if (msg.parent_id && messageMap.has(msg.parent_id)) {
      // Add as child to parent
      const parent = messageMap.get(msg.parent_id);
      if (parent && parent.children) {
        parent.children.push(threadedMsg);
      }
    } else {
      // This is a root message
      rootMessages.push(threadedMsg);
    }
  });
  
  // Sort each level by created_at
  const sortByDate = (a: ThreadedMessage, b: ThreadedMessage) => 
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  
  const sortChildrenRecursively = (msg: ThreadedMessage) => {
    if (msg.children && msg.children.length > 0) {
      msg.children.sort(sortByDate);
      msg.children.forEach(sortChildrenRecursively);
    }
  };
  
  rootMessages.sort(sortByDate);
  rootMessages.forEach(sortChildrenRecursively);
  
  return rootMessages;
}

/**
 * Get a complete thread starting from a root message
 */
export async function getThread(rootId: string) {
  try {
    // Fetch the root message
    const { data: rootMessage, error: rootError } = await supabase
      .from('messages')
      .select('*')
      .eq('id', rootId)
      .single();
      
    if (rootError) throw rootError;
    
    // Find all potential replies (using a LIKE query as workaround)
    // In a real implementation, you'd want to optimize this
    const { data: replies, error: repliesError } = await supabase
      .from('messages')
      .select('*')
      .like('content', `%[reply:${rootId}]%`)
      .order('created_at', { ascending: true });
      
    if (repliesError) throw repliesError;
    
    // Build thread tree
    return buildThreadTree([rootMessage, ...(replies || [])]);
  } catch (error) {
    console.error('Error fetching thread:', error);
    throw error;
  }
}

/**
 * Create a reply to a message
 */
export async function createReply(
  content: string, 
  parentId: string, 
  userId: string
) {
  try {
    // Get parent message to properly set up ancestors
    const { data: parentMessage, error: parentError } = await supabase
      .from('messages')
      .select('*')
      .eq('id', parentId)
      .single();
      
    if (parentError) throw parentError;
    
    // Parse parent's thread metadata
    const threadedParent = parseThreadMetadata(parentMessage);
    
    // Create ancestors array - parent's ancestors plus parent itself
    const ancestors = [...threadedParent.ancestors, parentId];
    
    // Add thread metadata to content
    const threadedContent = addThreadMetadata(content, parentId, ancestors);
    
    // Insert using existing messages table
    const { data: newMessage, error: insertError } = await supabase
      .from('messages')
      .insert([{
        content: threadedContent,
        user_id: userId,
        created_at: new Date().toISOString()
      }])
      .select()
      .single();
      
    if (insertError) throw insertError;
    
    return parseThreadMetadata(newMessage);
  } catch (error) {
    console.error('Error creating reply:', error);
    throw error;
  }
}
